/**
 * Navigation Service
 * Abstracts navigation for both web (React Router) and mobile (React Navigation)
 * This allows the same code to work in both environments
 */

type NavigationCallback = (path: string) => void;
type ReloadCallback = () => void;

class NavigationService {
  private static instance: NavigationService;
  private navigateCallback: NavigationCallback | null = null;
  private reloadCallback: ReloadCallback | null = null;
  private isWeb: boolean;

  private constructor() {
    // Detect if we're in a web environment
    this.isWeb = typeof window !== 'undefined' && typeof window.location !== 'undefined';
  }

  static getInstance(): NavigationService {
    if (!NavigationService.instance) {
      NavigationService.instance = new NavigationService();
    }
    return NavigationService.instance;
  }

  /**
   * Register navigation callback (called by App.tsx or mobile navigation setup)
   */
  setNavigateCallback(callback: NavigationCallback): void {
    this.navigateCallback = callback;
  }

  /**
   * Register reload callback (called by App.tsx or mobile app state handler)
   */
  setReloadCallback(callback: ReloadCallback): void {
    this.reloadCallback = callback;
  }

  /**
   * Navigate to a path
   * Uses React Router on web, React Navigation on mobile
   */
  navigate(path: string): void {
    if (this.navigateCallback) {
      this.navigateCallback(path);
    } else if (this.isWeb) {
      // Fallback for web: use window.location if callback not set
      window.location.href = path;
    } else {
      console.warn('Navigation callback not set. Cannot navigate to:', path);
    }
  }

  /**
   * Reload/refresh the app
   * On web: reloads the page
   * On mobile: triggers state refresh (no actual reload)
   */
  reload(): void {
    if (this.reloadCallback) {
      this.reloadCallback();
    } else if (this.isWeb) {
      // Fallback for web: use window.location.reload
      window.location.reload();
    } else {
      console.warn('Reload callback not set. Cannot reload app.');
    }
  }

  /**
   * Check if we're in a web environment
   */
  isWebEnvironment(): boolean {
    return this.isWeb;
  }
}

// Export singleton instance
export const navigationService = NavigationService.getInstance();






